import React, { useRef, useEffect, useState } from 'react';
import { getRandomEmoji } from 'random-emoji-picker';
import { getTextColor } from '../../utils/colorContrast';

interface MessageFrameProps {
  senderName: string;
  avatarUrl: string;
  avatarEmoji?: string;
  isEditing: boolean;
  content: string;
  backgroundColor?: string;
  onSenderNameChange: (name: string) => void;
  onSenderNameBlur: (name: string) => void;
  onContentChange: (content: string) => void;
  onContentBlur: (content: string) => void;
  onAvatarClick: () => void;
  onEmojiChange?: (emoji: string) => void;
}

export const MessageFrame: React.FC<MessageFrameProps> = ({
  senderName,
  avatarEmoji,
  isEditing,
  content,
  backgroundColor = '#E5E5EA',
  onSenderNameChange,
  onSenderNameBlur,
  onContentChange,
  onContentBlur,
  onAvatarClick,
  onEmojiChange
}) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [avatarSize, setAvatarSize] = useState(50);
  const [emoji, setEmoji] = useState(() => avatarEmoji || getRandomEmoji());
  const hasInitialized = useRef(false);

  // Save initial random emoji to database on mount
  useEffect(() => {
    if (!hasInitialized.current && !avatarEmoji && emoji) {
      onEmojiChange?.(emoji);
      hasInitialized.current = true;
    }
  }, []);

  // Sync emoji with prop when avatarEmoji changes
  useEffect(() => {
    if (avatarEmoji) {
      setEmoji(avatarEmoji);
    }
  }, [avatarEmoji]);

  const handleAvatarClick = () => {
    if (isEditing) {
      const newEmoji = getRandomEmoji();
      setEmoji(newEmoji);
      onEmojiChange?.(newEmoji);
    }
    onAvatarClick();
  };

  const handleAddBubble = () => {
    const newContent = content + '\nNew message';
    onContentChange(newContent);
  };

  const handleRemoveBubble = () => {
    if (displayBubbles.length > 1) {
      const newContent = displayBubbles.slice(0, -1).join('\n');
      onContentChange(newContent);
      onContentBlur(newContent);
    }
  };

  const handleBubbleBlur = (index: number, newText: string) => {
    const bubbles = [...displayBubbles];
    bubbles[index] = newText;
    const newContent = bubbles.join('\n');
    onContentBlur(newContent);
  };

  useEffect(() => {
    if (messageRef.current) {
      const height = messageRef.current.offsetHeight;
      const newSize = Math.min(80, Math.max(50, height * 0.8));
      setAvatarSize(newSize);
    }
  }, [content]);

  // Split content by newlines to create multiple bubbles
  const messageBubbles = content ? content.split('\n').filter(line => line.trim() !== '') : [];

  // If no bubbles after filtering, use the original content as a single bubble
  const displayBubbles = messageBubbles.length > 0 ? messageBubbles : [content || ''];

  return (
    <div className="message-item flex items-start gap-3 p-4">
      <div
        className="message-avatar flex-shrink-0 cursor-pointer rounded-full flex items-center justify-center"
        onClick={handleAvatarClick}
        style={{
          width: avatarSize,
          height: avatarSize,
          backgroundColor: '#F7BDB2',
          fontSize: avatarSize * 0.5
        }}
      >
        {emoji}
      </div>
      <div className="message-content flex-1">
        <div className="message-sender text-gray-400 text-sm mb-1">
          {isEditing ? (
            <input
              type="text"
              value={senderName}
              onChange={(e) => onSenderNameChange(e.target.value)}
              onBlur={(e) => onSenderNameBlur(e.target.value)}
              className="bg-transparent border-b border-gray-400 outline-none"
            />
          ) : (
            <span>{senderName}</span>
          )}
        </div>
        <div ref={messageRef}>
          <div className="flex flex-col gap-2 items-start">
            {displayBubbles.map((bubble, index) => (
              <div
                key={index}
                className="message-frame relative rounded-[18px] px-4 py-3 max-w-[80%]"
                style={{ backgroundColor }}
              >
                {index === 0 && (
                  <svg
                    className="absolute bottom-0"
                    width="28"
                    height="33"
                    viewBox="0 0 28 33"
                    fill="none"
                    style={{ left: '-9px', bottom: '-3px' }}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.5 19.5C9.5 14.4176 9 5.64706 9 0L28 19.0588C27.3095 21.1765 25.1 26.2588 21.7857 29.6471C18.4714 33.0353 8 32 0 32C3.45238 29.8824 9.5 27.5 9.5 19.5Z"
                      fill={backgroundColor}
                    />
                  </svg>
                )}
                <div
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  onBlur={(e) => handleBubbleBlur(index, e.currentTarget.textContent || '')}
                  dangerouslySetInnerHTML={{ __html: bubble }}
                  className="text-[17px] outline-none"
                  style={{
                    color: getTextColor(backgroundColor),
                    lineHeight: 1.4,
                    minHeight: '20px'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        {isEditing && (
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleAddBubble}
              className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors"
              title="Add message bubble"
            >
              <span className="text-xl leading-none">+</span>
            </button>
            <button
              onClick={handleRemoveBubble}
              disabled={displayBubbles.length <= 1}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                displayBubbles.length > 1
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="Remove last message bubble"
            >
              <span className="text-xl leading-none">−</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};